import React, { useEffect, useMemo, useState } from 'react';
import LecturerLayout from '../../components/LecturerLayout';
import axiosInstance from '../../utils/axiosInstance';

function fmtDate(s) {
  if (!s) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(s));
}

function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
      <svg className="h-8 w-8 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
      </svg>
      <p className="mt-3 text-sm">Loading shared courses…</p>
    </div>
  );
}

export default function LecturerSharedCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axiosInstance.get('/lecturer/shared-courses');
        if (!cancelled) setCourses(data.courses || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load shared courses.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return courses;
    const q = search.trim().toLowerCase();
    return courses.filter((c) => c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  }, [courses, search]);

  const openCourse = (courseId) => {
    window.open(`/lecturer/shared-courses/${courseId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <LecturerLayout title="Shared Courses">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-800">Shared Courses</h1>
            <p className="text-xs text-slate-500">Study material courses shared with you</p>
          </div>
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
            <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search courses…"
              className="w-48 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
            />
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 py-20 text-slate-400">
            <svg className="h-10 w-10 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <p className="mt-3 text-sm">
              {search ? 'No courses match your search.' : 'No study material courses have been shared with you yet.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((course) => (
              <div
                key={course.id}
                className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-lg"
              >
                <div className="h-1 w-full shrink-0 bg-emerald-400" />
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="mb-2 line-clamp-2 text-sm font-bold text-slate-800 leading-snug">{course.title}</h3>
                  {course.description && (
                    <p className="mb-3 line-clamp-3 text-xs text-slate-500">{course.description}</p>
                  )}
                  <div className="mt-auto flex items-center gap-3 border-t border-slate-100 pt-2 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h8" />
                      </svg>
                      {course.lessonCount} {course.lessonCount === 1 ? 'lesson' : 'lessons'}
                    </span>
                    <span className="ml-auto">Shared {fmtDate(course.sharedAt)}</span>
                  </div>
                </div>
                <div className="border-t border-slate-100 p-2">
                  <button
                    onClick={() => openCourse(course.id)}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                    Open Course
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </LecturerLayout>
  );
}
